/**
 * The detail page's ways in: a URL per port, and a login per persona.
 *
 * @module features/sandboxes/components/v2/detail/SandboxAccessSections
 */
import React from 'react';
import { Button, Flex, Heading, Text } from '@chatsdet/ui';
import { ArrowSquareOut, Eye, EyeSlash } from '@phosphor-icons/react';
import type { SandboxV2Persona } from '../../../hooks/useSandboxV2Detail';
import { CopyIconButton } from '../../../../../ui/components/CopyIconButton';

export function SandboxUrlsSection({ portUrls }: { portUrls: Record<string, string> }) {
  const ports = Object.entries(portUrls);
  if (ports.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-labelledby="sandbox-v2-urls">
      <Heading level={3} id="sandbox-v2-urls">
        URLs
      </Heading>
      <div className="divide-y divide-border">
        {ports.map(([name, url]) => (
          <Flex key={name} align="center" gap="sm" className="py-1.5">
            <Text weight="medium" className="w-28 shrink-0 truncate">
              {name}
            </Text>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-0 items-center gap-1 text-sm text-primary hover:underline"
            >
              <span className="truncate">{url}</span>
              <ArrowSquareOut size={12} className="shrink-0" />
            </a>
            <div className="ml-auto">
              <CopyIconButton value={url} label={`${name} URL`} />
            </div>
          </Flex>
        ))}
      </div>
    </section>
  );
}

function PersonaRow({ persona }: { persona: SandboxV2Persona }) {
  const [revealed, setRevealed] = React.useState(false);
  return (
    <Flex align="center" gap="sm" className="py-1.5">
      <Text weight="medium" className="w-28 shrink-0 truncate">
        {persona.name}
      </Text>
      <Flex align="center" gap="xs" className="min-w-0 flex-1">
        <Text size="sm" className="truncate">
          {persona.email}
        </Text>
        <CopyIconButton value={persona.email} label={`${persona.name} email`} />
      </Flex>
      <Flex align="center" gap="xs" className="w-56 shrink-0">
        <Text size="sm" className="min-w-0 flex-1 truncate font-mono" data-testid={`password-${persona.name}`}>
          {revealed ? persona.password : '••••••••••'}
        </Text>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={`${revealed ? 'Hide' : 'Reveal'} ${persona.name} password`}
          onPress={() => setRevealed((value) => !value)}
        >
          {revealed ? <EyeSlash size={14} /> : <Eye size={14} />}
        </Button>
        <CopyIconButton value={persona.password} label={`${persona.name} password`} />
      </Flex>
    </Flex>
  );
}

export function SandboxLoginsSection({ personas }: { personas: SandboxV2Persona[] }) {
  if (personas.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-labelledby="sandbox-v2-logins">
      <Heading level={3} id="sandbox-v2-logins">
        Logins
      </Heading>
      <div className="divide-y divide-border">
        {personas.map((persona) => (
          <PersonaRow key={persona.name} persona={persona} />
        ))}
      </div>
    </section>
  );
}
